import type {
  ArchiveDemoNoteTemplateRequest,
  CreateDemoNoteTemplateRequest,
  DemoNoteTemplate,
  DemoNoteTemplateDefaults,
  DemoNoteTemplatesState,
  DuplicateDemoNoteTemplateRequest,
  RestoreDemoNoteTemplateRequest,
  UpdateDemoNoteTemplateDefaultsRequest,
  UpdateDemoNoteTemplateRequest,
} from "@emr/contracts";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type DemoNoteTemplatesClient, demoNoteTemplatesClient } from "./demoNoteTemplatesClient";

interface DemoNoteTemplatesContextValue {
  readonly state: DemoNoteTemplatesState | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  refresh(): Promise<void>;
  createTemplate(input: CreateDemoNoteTemplateRequest): Promise<DemoNoteTemplate>;
  updateTemplate(
    templateId: string,
    input: UpdateDemoNoteTemplateRequest,
  ): Promise<DemoNoteTemplate>;
  duplicateTemplate(
    templateId: string,
    input: DuplicateDemoNoteTemplateRequest,
  ): Promise<DemoNoteTemplate>;
  archiveTemplate(
    templateId: string,
    input: ArchiveDemoNoteTemplateRequest,
  ): Promise<DemoNoteTemplate>;
  restoreTemplate(
    templateId: string,
    input: RestoreDemoNoteTemplateRequest,
  ): Promise<DemoNoteTemplate>;
  updateDefaults(input: UpdateDemoNoteTemplateDefaultsRequest): Promise<DemoNoteTemplateDefaults>;
}

const DemoNoteTemplatesContext = createContext<DemoNoteTemplatesContextValue | null>(null);

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "The synthetic note template library could not be updated.";
}

export function DemoNoteTemplatesProvider({
  children,
  client = demoNoteTemplatesClient,
  pollIntervalMs = 4_000,
}: {
  readonly children: ReactNode;
  readonly client?: DemoNoteTemplatesClient;
  readonly pollIntervalMs?: number;
}) {
  const [state, setState] = useState<DemoNoteTemplatesState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const next = await client.getState();
      if (!mounted.current) return;
      setState(next);
      setError(null);
    } catch (caught) {
      if (mounted.current) setError(errorMessage(caught));
      throw caught;
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, [client]);

  useEffect(() => {
    mounted.current = true;
    void refresh().catch(() => undefined);
    const interval =
      pollIntervalMs > 0
        ? window.setInterval(() => void refresh().catch(() => undefined), pollIntervalMs)
        : undefined;
    return () => {
      mounted.current = false;
      if (interval !== undefined) window.clearInterval(interval);
    };
  }, [pollIntervalMs, refresh]);

  const replaceTemplate = useCallback((template: DemoNoteTemplate) => {
    setState((current) =>
      current === null
        ? current
        : {
            ...current,
            templates: current.templates.map((candidate) =>
              candidate.id === template.id ? template : candidate,
            ),
          },
    );
  }, []);

  const createTemplate = useCallback(
    async (input: CreateDemoNoteTemplateRequest) => {
      try {
        const template = await client.createTemplate(input);
        if (mounted.current) {
          setState((current) =>
            current === null
              ? current
              : current.templates.some((candidate) => candidate.id === template.id)
                ? current
                : { ...current, templates: [...current.templates, template] },
          );
          setError(null);
        }
        return template;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client],
  );

  const updateTemplate = useCallback(
    async (templateId: string, input: UpdateDemoNoteTemplateRequest) => {
      try {
        const template = await client.updateTemplate(templateId, input);
        if (mounted.current) {
          replaceTemplate(template);
          setError(null);
        }
        return template;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client, replaceTemplate],
  );

  const duplicateTemplate = useCallback(
    async (templateId: string, input: DuplicateDemoNoteTemplateRequest) => {
      try {
        const template = await client.duplicateTemplate(templateId, input);
        if (mounted.current) {
          setState((current) =>
            current === null
              ? current
              : current.templates.some((candidate) => candidate.id === template.id)
                ? current
                : { ...current, templates: [...current.templates, template] },
          );
          setError(null);
        }
        return template;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client],
  );

  const archiveTemplate = useCallback(
    async (templateId: string, input: ArchiveDemoNoteTemplateRequest) => {
      try {
        const template = await client.archiveTemplate(templateId, input);
        if (mounted.current) {
          replaceTemplate(template);
          setError(null);
        }
        return template;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client, replaceTemplate],
  );

  const restoreTemplate = useCallback(
    async (templateId: string, input: RestoreDemoNoteTemplateRequest) => {
      try {
        const template = await client.restoreTemplate(templateId, input);
        if (mounted.current) {
          replaceTemplate(template);
          setError(null);
        }
        return template;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client, replaceTemplate],
  );

  const updateDefaults = useCallback(
    async (input: UpdateDemoNoteTemplateDefaultsRequest) => {
      try {
        const defaults = await client.updateDefaults(input);
        if (mounted.current) {
          setState((current) =>
            current === null ? current : { ...current, visitTypeDefaults: defaults },
          );
          setError(null);
        }
        return defaults;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client],
  );

  const value = useMemo<DemoNoteTemplatesContextValue>(
    () => ({
      state,
      isLoading,
      error,
      refresh,
      createTemplate,
      updateTemplate,
      duplicateTemplate,
      archiveTemplate,
      restoreTemplate,
      updateDefaults,
    }),
    [
      archiveTemplate,
      createTemplate,
      duplicateTemplate,
      error,
      isLoading,
      refresh,
      restoreTemplate,
      state,
      updateDefaults,
      updateTemplate,
    ],
  );

  return (
    <DemoNoteTemplatesContext.Provider value={value}>{children}</DemoNoteTemplatesContext.Provider>
  );
}

export function useDemoNoteTemplates(): DemoNoteTemplatesContextValue {
  const context = useContext(DemoNoteTemplatesContext);
  if (context === null) {
    throw new Error("useDemoNoteTemplates must be used inside DemoNoteTemplatesProvider");
  }
  return context;
}

export function useOptionalDemoNoteTemplates(): DemoNoteTemplatesContextValue | null {
  return useContext(DemoNoteTemplatesContext);
}

export function createDemoNoteTemplateIdempotencyKey(prefix: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${random}`;
}
